import { useCallback, useState } from "react";
import { toast } from "sonner";

import { getChannels, getOrganisations } from "api/endpoints";
import type { Channel, Organisation } from "types";

export const useConfigure = () => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [activeOrganisationId, setActiveOrganisationId] = useState<number | null>(null);
  const [activeChannelId, setActiveChannelId] = useState<number | null>(null);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [isLoadingOrganisations, setIsLoadingOrganisations] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchOrganisations = useCallback(async () => {
    setIsLoadingOrganisations(true);
    setErrorMessage("");

    try {
      const [organisations, error] = await getOrganisations();

      if (error) {
        setErrorMessage(error);
        toast.error(error);
        return;
      }

      if (organisations) {
        setOrganisations(organisations);
        const firstOrgId = organisations[0]?.id ?? null;
        setActiveOrganisationId(firstOrgId);
      }
    } catch {
      const errorMessage = "Failed to fetch organisations. Please try again.";
      setErrorMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoadingOrganisations(false);
    }
  }, []);

  const fetchChannels = useCallback(async (organisationId: number) => {
    setIsLoadingChannels(true);
    setErrorMessage("");

    if (!organisationId) {
      setChannels([]);
      setActiveChannelId(null);
      return;
    }

    try {
      const [channels, error] = await getChannels(organisationId);

      if (error) {
        setErrorMessage(error);
        toast.error(error);
        return;
      }

      if (channels) {
        setChannels(channels);
        const firstChannelId = channels[0]?.id ?? null;
        setActiveChannelId(firstChannelId);
      }
    } catch {
      const errorMessage = "Failed to load channels. Please try again.";
      setErrorMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoadingChannels(false);
    }
  }, []);

  const handleSetActiveOrganisationId = useCallback((id: number | null) => {
    setActiveOrganisationId(id);
    setActiveChannelId(null);
    setChannels([]);
    setErrorMessage("");
  }, []);

  const handleSetActiveChannelId = useCallback((id: number | null) => {
    setActiveChannelId(id);

    setErrorMessage("");
  }, []);

  return {
    fetchOrganisations,
    organisations,
    activeOrganisationId,
    setActiveOrganisationId: handleSetActiveOrganisationId,
    isLoadingOrganisations,
    fetchChannels,
    channels,
    activeChannelId,
    setActiveChannelId: handleSetActiveChannelId,
    isLoadingChannels,
    errorMessage,
  };
};
